import { __ } from '@wordpress/i18n';
import { RawHTML, useEffect } from "@wordpress/element";
import { useBlockProps, InnerBlocks, InspectorControls, ColorPalette } from '@wordpress/block-editor';
import { __experimentalHeading as Heading, PanelBody, __experimentalUnitControl as UnitControl, SelectControl } from '@wordpress/components';
import "./list-item";

export const edit = ({ clientId, attributes, setAttributes }) => {
    const {
        id,
        hfcIconColor,
        hfcIconHoverColor,
        hfcItemFontColor,
        hfcItemFontHoverColor,
        hfcIconBgColor,
        hfcIconBgHoverColor,
        hfcIconSize,
        hfcItemFontSize,
        hfcIconBorderWidth,
        hfcIconBorderStyle,
        hfcIconBorderColor,
        hfcIconBorderHoverColor,
        hfcItemPadding,
        hfcContentGap,
        hfcItemGap,
        hfcBorderRadius,
        hfcLayoutDirection,
        hfcAlignment,
    } = attributes;

    useEffect(() => {
        if (!id) {
            // Set unique ID through block index only once.
            let clientIdSliced = clientId.slice(clientId.lastIndexOf("-") + 1);
            setAttributes({ id: 'hfc-icon-list-' + clientIdSliced });
        }
    }, [id, clientId, setAttributes]);

    // Define Block Props.
    const blockProps = useBlockProps();

    const hfcIlDynamicStyles = `
    .editor-styles-wrapper #${id} .block-editor-inner-blocks > .block-editor-block-list__layout {
        display: flex;
        flex-direction: ${hfcLayoutDirection === 'horizontal' ? 'row' : 'column'};
        gap: ${hfcItemGap};
        justify-content: ${hfcAlignment};
    }
    .editor-styles-wrapper #${id} a {
        text-decoration: none;
    }
    .editor-styles-wrapper #${id} .wp-block-hfc-blocks-list-item {
        display: flex;
        align-items: center;
        justify-content: ${hfcAlignment};
        padding: 0px;
    }
    .editor-styles-wrapper #${id} .fp-ip-inner {
        display: inline-flex;
        align-items: center;
        gap: ${hfcContentGap};
    }
    .editor-styles-wrapper #${id} span {
        margin: 0px;
        font-size: ${hfcItemFontSize};    
    }
    .editor-styles-wrapper #${id}.hfc-icon-list-main .fp-ip-inner .fp-ip-icon{
        display: flex;
        background-color: ${hfcIconBgColor};
        transition: 0.4s;
        padding: ${hfcItemPadding};
        border-radius: ${hfcBorderRadius};
        border-width: ${hfcIconBorderWidth};
        border-style: ${hfcIconBorderStyle};
        border-color: ${hfcIconBorderColor};
    }
    .editor-styles-wrapper #${id}.hfc-icon-list-main .fp-ip-inner:hover .fp-ip-icon{
        background-color: ${hfcIconBgHoverColor};
        border-color: ${hfcIconBorderHoverColor};
    }
    .editor-styles-wrapper #${id}.hfc-icon-list-main .fp-ip-inner svg {
        color: ${hfcIconColor};
        font-size: ${hfcIconSize};
        height: ${hfcIconSize};
        width: ${hfcIconSize};
        flex-shrink: 0;
    }
    .editor-styles-wrapper #${id}.hfc-icon-list-main .fp-ip-inner:hover svg {
        color: ${hfcIconHoverColor};
    }
    .editor-styles-wrapper #${id} .fp-ip-inner span{
        color: ${hfcItemFontColor};
        line-height: 2;
    }
    .editor-styles-wrapper #${id} .fp-ip-inner:hover span {
        color: ${hfcItemFontHoverColor};
    }
    /* Edit page compatibility */
    .editor-styles-wrapper .hfc-icon-list-main .block-editor-block-list__block,
    .editor-styles-wrapper .hfc-icon-list-main .wp-block {
        margin: 0px;
    }`;

    return (
        <>
            <InspectorControls key={`inspector-controls-${id}`}>
                <PanelBody
                    title={__('Appearance')}
                    icon={'admin-generic'}
                    initialOpen={true}
                    className={'frhd__control frhd__control-query'}
                    >
                    <UnitControl
                        label={__('Icon Size :')}
                        onChange={newValue => setAttributes({ hfcIconSize: newValue })}
                        isUnitSelectTabbable
                        value={hfcIconSize}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <UnitControl
                        label={__('Icon Background Size :')}
                        onChange={newValue => setAttributes({ hfcItemPadding: newValue })}
                        isUnitSelectTabbable
                        value={hfcItemPadding}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <UnitControl
                        label={__('Icon Background Border Radius :')}
                        value={hfcBorderRadius}
                        onChange={(newValue) => setAttributes({ hfcBorderRadius: newValue })}
                        isUnitSelectTabbable
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <UnitControl
                        label={__('Label Size :')}
                        onChange={newValue => setAttributes({ hfcItemFontSize: newValue })}
                        isUnitSelectTabbable
                        value={hfcItemFontSize}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <UnitControl
                        label={__('Gap Between Icon & Label:')}
                        onChange={newValue => setAttributes({ hfcContentGap: newValue })}
                        isUnitSelectTabbable
                        value={hfcContentGap}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <UnitControl
                        label={__('Items Gap:')}
                        onChange={newValue => setAttributes({ hfcItemGap: newValue })}
                        isUnitSelectTabbable
                        value={hfcItemGap}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <SelectControl
                        label={__('Select Layout Direction')}
                        value={hfcLayoutDirection}
                        options={[
                            { label: __('Horizontal'), value: 'horizontal' },
                            { label: __('Vertical'), value: 'vertical' }
                        ]}
                        onChange={(newValue) => setAttributes({ hfcLayoutDirection: newValue })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__('Alignment')}
                        value={hfcAlignment}
                        options={[
                            { label: __('Left'), value: 'left' },
                            { label: __('Center'), value: 'center' },
                            { label: __('Right'), value: 'right' },
                        ]}
                        onChange={(newValue) => setAttributes({ hfcAlignment: newValue })}
                        __nextHasNoMarginBottom
                    />
                    <Heading className={'frhd__pb-heading'}>Icon Color :</Heading>
                    <ColorPalette
                        value={hfcIconColor}
                        onChange={(colorValue) => setAttributes({ hfcIconColor: colorValue })}
                        clearable={false}
                    />
                    <Heading className={'frhd__pb-heading'}>Icon Hover Color :</Heading>
                    <ColorPalette
                        value={hfcIconHoverColor}
                        onChange={(colorValue) => setAttributes({ hfcIconHoverColor: colorValue })}
                        clearable={false}
                    />
                    <Heading className={'frhd__pb-heading'}>Label Color :</Heading>
                    <ColorPalette
                        value={hfcItemFontColor}
                        onChange={(colorValue) => setAttributes({ hfcItemFontColor: colorValue })}
                        clearable={false}
                    />
                    <Heading className={'frhd__pb-heading'}>Label Hover Color :</Heading>
                    <ColorPalette
                        value={hfcItemFontHoverColor}
                        onChange={(colorValue) => setAttributes({ hfcItemFontHoverColor: colorValue })}
                        clearable={false}
                    />
                    <Heading className={'frhd__pb-heading'}>Icon Background Color :</Heading>
                    <ColorPalette
                        value={hfcIconBgColor}
                        onChange={(colorValue) => setAttributes({ hfcIconBgColor: colorValue })}
                        clearable={false}
                    />
                    <Heading className={'frhd__pb-heading'}>Icon Background Hover Color :</Heading>
                    <ColorPalette
                        value={hfcIconBgHoverColor}
                        onChange={(colorValue) => setAttributes({ hfcIconBgHoverColor: colorValue })}
                        clearable={false}
                    />
                </PanelBody>
                <PanelBody
                    title={__('Icon Border')}
                    icon={'admin-generic'}
                    initialOpen={true}
                    className={'frhd__control frhd__control-query'}
                >
                    <UnitControl
                        label={__('Border Width :')}
                        onChange={(newValue) => setAttributes({ hfcIconBorderWidth: newValue })}
                        __nextHasNoMarginBottom
                        isUnitSelectTabbable
                        value={hfcIconBorderWidth}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <SelectControl
                        label={__('Border Style :')}
                        value={hfcIconBorderStyle}
                        options={[
                            { label: __('None'), value: 'none' },
                            { label: __('Solid'), value: 'solid' },
                            { label: __('Dashed'), value: 'dashed' },
                            { label: __('Dotted'), value: 'dotted' },
                            { label: __('Double'), value: 'double' },
                        ]}
                        onChange={(newValue) => setAttributes({ hfcIconBorderStyle: newValue })}
                        __nextHasNoMarginBottom
                    />
                    <Heading className={'frhd__pb-heading'}>Border Color :</Heading>
                    <ColorPalette
                        value={hfcIconBorderColor}
                        onChange={(colorValue) => setAttributes({ hfcIconBorderColor: colorValue })}
                        clearable={false}
                    />

                    <Heading className={'frhd__pb-heading'}>Border Hover Color :</Heading>
                    <ColorPalette
                        value={hfcIconBorderHoverColor}
                        onChange={(colorValue) => setAttributes({ hfcIconBorderHoverColor: colorValue })}
                        clearable={false}
                    />
                </PanelBody>
            </InspectorControls>
            <div {...blockProps} key={`${id}-main-blocks`}>
                <RawHTML>{`<style>${hfcIlDynamicStyles}</style>`}</RawHTML>
                <div id={id} className={'hfc-icon-list-main'}>
                    <InnerBlocks
                        allowedBlocks={["hfc-blocks/list-item"]}
                        template={[
                            ["hfc-blocks/list-item"],
                            ["hfc-blocks/list-item"],
                            ["hfc-blocks/list-item"]
                        ]}
                    />
                </div>
            </div>
        </>
    );
};


export const save = ({ attributes }) => {

    const {
        id,
        hfcIconColor,
        hfcIconHoverColor,
        hfcItemFontColor,
        hfcItemFontHoverColor,
        hfcIconBgColor,
        hfcIconBgHoverColor,
        hfcIconSize,
        hfcIconBorderWidth,
        hfcIconBorderStyle,
        hfcIconBorderColor,
        hfcIconBorderHoverColor,
        hfcItemFontSize,
        hfcItemPadding,
        hfcContentGap,
        hfcItemGap,
        hfcBorderRadius,
        hfcLayoutDirection,
        hfcAlignment
    } = attributes;

    const hfcIlDynamicStyles = `#${id}.hfc-icon-list-main{
        display: flex;
        flex-direction: ${hfcLayoutDirection === 'horizontal' ? 'row' : 'column'};
        gap: ${hfcItemGap};
        justify-content: ${hfcAlignment};
    }
    #${id}.hfc-icon-list-main .wp-block-hfc-blocks-list-item{
        display: flex;
        align-items: center;
        gap: ${hfcContentGap};
        justify-content: ${hfcAlignment};
        text-decoration: none;
    }
    #${id}.hfc-icon-list-main a{
        text-decoration: none;
    }
    #${id}.hfc-icon-list-main .fp-ip-inner{
        display: inline-flex;
        align-items: center;
        gap: ${hfcContentGap};
    }
    #${id}.hfc-icon-list-main span{
        margin: 0px;
        font-size: ${hfcItemFontSize};
    }
    #${id}.hfc-icon-list-main .fp-ip-inner .fp-ip-icon{
        display: flex;
        background-color: ${hfcIconBgColor};
        transition: 0.4s;
        padding: ${hfcItemPadding};
        border-radius: ${hfcBorderRadius};
        border-width: ${hfcIconBorderWidth};
        border-style: ${hfcIconBorderStyle};
        border-color: ${hfcIconBorderColor};
     }
    #${id}.hfc-icon-list-main .fp-ip-inner:hover .fp-ip-icon{
        background-color: ${hfcIconBgHoverColor};
        border-color: ${hfcIconBorderHoverColor};
     }
    #${id}.hfc-icon-list-main .fp-ip-inner svg{
        width: ${hfcIconSize};
        height: ${hfcIconSize};
        color: ${hfcIconColor};
        flex-shrink: 0;
     }
    #${id}.hfc-icon-list-main .fp-ip-inner:hover svg{
        color: ${hfcIconHoverColor};
     }
    #${id}.hfc-icon-list-main .fp-ip-inner span{
        color: ${hfcItemFontColor};
        line-height: 2;
    }
     #${id}.hfc-icon-list-main .fp-ip-inner:hover span{
        color: ${hfcItemFontHoverColor};
    }`;

    return (
        <>
            <RawHTML>{`<style>${hfcIlDynamicStyles}</style>`}</RawHTML>
            <div id={id} className={'hfc-icon-list-main'}>
                    <InnerBlocks.Content />
            </div>
        </>
    )
};
